// apps/admin-ui/src/pages/BlockExplorerPage.tsx
// Anchor chain block explorer — view blocks and their transactions
// ALL RIGHTS RESERVED
//
// UPDATED 2026-02-05: Switched to live data via /api/v1/admin/anchor/blocks

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import '../styles/data-table.css';
import '../styles/block-explorer.css';

// =============================================================================
// TYPES
// =============================================================================

interface AnchorBlock {
  id: string;
  sequence: number;
  txCount: number;
  txVolume: string;
  feeTotal: string;
  reserveBalance: string;
  totalSupply: string;
  merkleRoot: string;
  anchorHash: string;
  prevHash: string;
  createdAt: string;
}

interface BlockTransaction {
  id: string;
  txType: 'mint' | 'burn' | 'transfer' | 'reversal';
  amount: string;
  fee: string;
  feeCxxr: string;
  feeCcc: string;
  feeReserve: string;
  status: string;
  fromAccountId: string | null;
  toAccountId: string | null;
  memo: string | null;
  externalRef: string | null;
  createdAt: string;
  processedAt: string | null;
}

// =============================================================================
// HELPERS
// =============================================================================

function formatCurrency(value: string | number): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (num >= 1_000_000_000) {
    return `$${(num / 1_000_000_000).toFixed(2)}B`;
  }
  if (num >= 1_000_000) {
    return `$${(num / 1_000_000).toFixed(2)}M`;
  }
  if (num >= 1_000) {
    return `$${(num / 1_000).toFixed(2)}K`;
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

function formatCurrencyFull(value: string | number): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDateShort(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function truncateHash(hash: string, len = 12): string {
  if (!hash || hash.length <= len + 6) return hash;
  return `${hash.slice(0, len)}…${hash.slice(-4)}`;
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const hours = Math.floor(diff / 3_600_000);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ${hours % 24}h ago`;
  if (hours > 0) return `${hours}h ago`;
  const mins = Math.floor(diff / 60_000);
  return `${mins}m ago`;
}

function txTypeBadgeClass(txType: string): string {
  switch (txType) {
    case 'mint':
      return 'be-badge--mint';
    case 'burn':
      return 'be-badge--burn';
    case 'transfer':
      return 'be-badge--transfer';
    case 'reversal':
      return 'be-badge--reversal';
    default:
      return '';
  }
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'completed':
      return 'status-badge status-completed';
    case 'failed':
      return 'status-badge status-failed';
    case 'queued':
    case 'processing':
      return 'status-badge status-pending';
    case 'cancelled':
      return 'status-badge status-dismissed';
    default:
      return 'status-badge';
  }
}

// =============================================================================
// COMPONENT
// =============================================================================

export function BlockExplorerPage() {
  // Block list state
  const [blocks, setBlocks] = useState<AnchorBlock[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [limit, setLimit] = useState(30);

  // Detail view state
  const [selectedBlock, setSelectedBlock] = useState<AnchorBlock | null>(null);
  const [transactions, setTransactions] = useState<BlockTransaction[]>([]);
  const [txLoading, setTxLoading] = useState(false);
  const [txError, setTxError] = useState<string | null>(null);

  // ─── Fetch blocks ─────────────────────────────────────────────────

  const fetchBlocks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get<{ blocks: AnchorBlock[]; count: number }>(
        `/api/v1/admin/anchor/blocks?limit=${limit}`
      );
      setBlocks(response.blocks || []);
    } catch (err: any) {
      console.error('Failed to fetch blocks:', err);
      setError(err.message || 'Failed to load blocks');
      setBlocks([]);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchBlocks();
  }, [fetchBlocks]);

  // ─── Fetch transactions for a block ───────────────────────────────

  const fetchTransactions = useCallback(async (block: AnchorBlock) => {
    setSelectedBlock(block);
    setTxLoading(true);
    setTxError(null);

    try {
      const response = await api.get<{ transactions: BlockTransaction[]; count: number }>(
        `/api/v1/admin/anchor/blocks/${block.id}/transactions`
      );
      setTransactions(response.transactions || []);
    } catch (err: any) {
      console.error('Failed to fetch block transactions:', err);
      setTxError(err.message || 'Failed to load transactions');
      setTransactions([]);
    } finally {
      setTxLoading(false);
    }
  }, []);

  const handleBackToList = () => {
    setSelectedBlock(null);
    setTransactions([]);
    setTxError(null);
  };

  // ─── Chain summary metrics ────────────────────────────────────────

  const chainLength = blocks.length;
  const latestBlock = blocks.length > 0 ? blocks[0] : null;
  const genesisBlock = blocks.length > 0 ? blocks[blocks.length - 1] : null;
  const totalTx = blocks.reduce((sum, b) => sum + b.txCount, 0);

  // ═════════════════════════════════════════════════════════════════════
  // BLOCK DETAIL VIEW
  // ═════════════════════════════════════════════════════════════════════

  if (selectedBlock) {
    return (
      <div className="data-table-page">
        {/* Header */}
        <div className="table-header">
          <div className="header-left">
            <button className="be-back-btn" onClick={handleBackToList}>
              ← Blocks
            </button>
            <h1>
              Block #{selectedBlock.sequence}
              {selectedBlock.sequence === 0 && (
                <span className="be-genesis-tag">Genesis</span>
              )}
            </h1>
          </div>
          <div className="header-right">
            <span className="be-block-time">
              {formatDateTime(selectedBlock.createdAt)}
            </span>
          </div>
        </div>

        {/* Block summary card */}
        <div className="be-block-detail">
          <div className="be-detail-grid">
            <div className="be-detail-item">
              <span className="be-detail-label">Sequence</span>
              <span className="be-detail-value">{selectedBlock.sequence}</span>
            </div>
            <div className="be-detail-item">
              <span className="be-detail-label">Transactions</span>
              <span className="be-detail-value">{selectedBlock.txCount}</span>
            </div>
            <div className="be-detail-item">
              <span className="be-detail-label">Volume</span>
              <span className="be-detail-value">{formatCurrencyFull(selectedBlock.txVolume)}</span>
            </div>
            <div className="be-detail-item">
              <span className="be-detail-label">Fees</span>
              <span className="be-detail-value">{formatCurrencyFull(selectedBlock.feeTotal)}</span>
            </div>
            <div className="be-detail-item">
              <span className="be-detail-label">Reserve</span>
              <span className="be-detail-value">{formatCurrency(selectedBlock.reserveBalance)}</span>
            </div>
            <div className="be-detail-item">
              <span className="be-detail-label">Supply</span>
              <span className="be-detail-value">{formatCurrency(selectedBlock.totalSupply)}</span>
            </div>
          </div>

          {/* Hashes */}
          <div className="be-hashes">
            <div className="be-hash-row">
              <span className="be-hash-label">Anchor Hash</span>
              <code className="be-hash-value">{selectedBlock.anchorHash}</code>
            </div>
            <div className="be-hash-row">
              <span className="be-hash-label">Merkle Root</span>
              <code className="be-hash-value">{selectedBlock.merkleRoot}</code>
            </div>
            <div className="be-hash-row">
              <span className="be-hash-label">Prev Hash</span>
              <code className="be-hash-value">{selectedBlock.prevHash}</code>
            </div>
          </div>
        </div>

        {/* Transactions table */}
        <div className="be-section-header">
          <h2>Transactions ({selectedBlock.txCount})</h2>
        </div>

        {txError && (
          <div className="table-error">
            <p>{txError}</p>
          </div>
        )}

        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: '80px' }}>ID</th>
                <th style={{ width: '90px' }}>Type</th>
                <th style={{ width: '140px' }}>Amount</th>
                <th style={{ width: '100px' }}>Fee</th>
                <th style={{ width: '90px' }}>Status</th>
                <th>From</th>
                <th>To</th>
                <th>Memo</th>
                <th style={{ width: '150px' }}>Created</th>
              </tr>
            </thead>
            <tbody>
              {txLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <tr key={`skel-${i}`}>
                    {Array.from({ length: 9 }).map((_, j) => (
                      <td key={j}><div className="skeleton-cell" /></td>
                    ))}
                  </tr>
                ))
              ) : transactions.length === 0 ? (
                <tr>
                  <td colSpan={9} className="empty-row">
                    {selectedBlock.sequence === 0
                      ? 'Genesis block — no user transactions'
                      : 'No transactions in this block'}
                  </td>
                </tr>
              ) : (
                transactions.map((tx) => (
                  <tr key={tx.id}>
                    <td className="be-mono">{tx.id}</td>
                    <td>
                      <span className={`be-badge ${txTypeBadgeClass(tx.txType)}`}>
                        {tx.txType}
                      </span>
                    </td>
                    <td className="be-amount">{formatCurrencyFull(tx.amount)}</td>
                    <td className="be-mono be-fee">
                      {parseFloat(tx.fee) > 0 ? formatCurrencyFull(tx.fee) : '—'}
                    </td>
                    <td>
                      <span className={statusBadgeClass(tx.status)}>
                        {tx.status}
                      </span>
                    </td>
                    <td className="be-mono">
                      {tx.fromAccountId ? `#${tx.fromAccountId}` : '—'}
                    </td>
                    <td className="be-mono">
                      {tx.toAccountId ? `#${tx.toAccountId}` : '—'}
                    </td>
                    <td className="be-memo" title={tx.memo || undefined}>
                      {tx.memo || <span className="cell-empty">—</span>}
                    </td>
                    <td className="be-time">{formatDateTime(tx.createdAt)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Fee breakdown (only if transactions present) */}
        {transactions.length > 0 && transactions.some(t => parseFloat(t.fee) > 0) && (
          <div className="be-fee-summary">
            <h3>Fee Distribution</h3>
            <div className="be-fee-grid">
              <div className="be-fee-item">
                <span className="be-fee-label">Total Fees</span>
                <span className="be-fee-value">
                  {formatCurrencyFull(
                    transactions.reduce((s, t) => s + parseFloat(t.fee), 0).toString()
                  )}
                </span>
              </div>
              <div className="be-fee-item">
                <span className="be-fee-label">→ CXXR</span>
                <span className="be-fee-value">
                  {formatCurrencyFull(
                    transactions.reduce((s, t) => s + parseFloat(t.feeCxxr), 0).toString()
                  )}
                </span>
              </div>
              <div className="be-fee-item">
                <span className="be-fee-label">→ CCC</span>
                <span className="be-fee-value">
                  {formatCurrencyFull(
                    transactions.reduce((s, t) => s + parseFloat(t.feeCcc), 0).toString()
                  )}
                </span>
              </div>
              <div className="be-fee-item">
                <span className="be-fee-label">→ Reserve</span>
                <span className="be-fee-value">
                  {formatCurrencyFull(
                    transactions.reduce((s, t) => s + parseFloat(t.feeReserve), 0).toString()
                  )}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ═════════════════════════════════════════════════════════════════════
  // BLOCK LIST VIEW
  // ═════════════════════════════════════════════════════════════════════

  return (
    <div className="data-table-page">
      {/* Header */}
      <div className="table-header">
        <div className="header-left">
          <Link to="/" className="back-link">← Dashboard</Link>
          <h1>Block Explorer</h1>
        </div>
        <div className="header-right">
          <button onClick={fetchBlocks} className="export-btn">
            Refresh
          </button>
        </div>
      </div>

      {/* Chain summary */}
      <div className="be-chain-summary">
        <div className="be-summary-card">
          <span className="be-summary-label">Chain Length</span>
          <span className="be-summary-value">{chainLength}</span>
          <span className="be-summary-unit">blocks</span>
        </div>
        <div className="be-summary-card">
          <span className="be-summary-label">Total Transactions</span>
          <span className="be-summary-value">{totalTx.toLocaleString()}</span>
          <span className="be-summary-unit">anchored</span>
        </div>
        <div className="be-summary-card">
          <span className="be-summary-label">Latest Block</span>
          <span className="be-summary-value">
            {latestBlock ? `#${latestBlock.sequence}` : '—'}
          </span>
          <span className="be-summary-unit">
            {latestBlock ? timeAgo(latestBlock.createdAt) : ''}
          </span>
        </div>
        <div className="be-summary-card">
          <span className="be-summary-label">Genesis</span>
          <span className="be-summary-value">
            {genesisBlock ? formatDateShort(genesisBlock.createdAt) : '—'}
          </span>
          <span className="be-summary-unit">
            {genesisBlock ? new Date(genesisBlock.createdAt).toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
            }) : ''}
          </span>
        </div>
      </div>

      {error && (
        <div className="table-error">
          <p>{error}</p>
        </div>
      )}

      {/* Block table */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: '70px' }}>Seq</th>
              <th style={{ width: '130px' }}>Date</th>
              <th style={{ width: '60px', textAlign: 'right' }}>TXs</th>
              <th style={{ width: '120px', textAlign: 'right' }}>Volume</th>
              <th style={{ width: '100px', textAlign: 'right' }}>Fees</th>
              <th style={{ width: '120px', textAlign: 'right' }}>Reserve</th>
              <th style={{ width: '120px', textAlign: 'right' }}>Supply</th>
              <th>Anchor Hash</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={`skel-${i}`}>
                  {Array.from({ length: 8 }).map((_, j) => (
                    <td key={j}><div className="skeleton-cell" /></td>
                  ))}
                </tr>
              ))
            ) : blocks.length === 0 ? (
              <tr>
                <td colSpan={8} className="empty-row">
                  No blocks found. Run <code>cxctl anchor genesis --reserve 400000000 --deposits ~/cx/cxxr/tools/cxctl/data/default-genesis-deposits.json</code> to create the genesis block.
                </td>
              </tr>
            ) : (
              blocks.map((block) => (
                <tr
                  key={block.id}
                  className="be-block-row"
                  onClick={() => fetchTransactions(block)}
                >
                  <td>
                    <span className="be-seq">
                      {block.sequence}
                      {block.sequence === 0 && (
                        <span className="be-genesis-dot" title="Genesis" />
                      )}
                    </span>
                  </td>
                  <td className="be-time">{formatDateTime(block.createdAt)}</td>
                  <td className="be-num">{block.txCount}</td>
                  <td className="be-num">{formatCurrency(block.txVolume)}</td>
                  <td className="be-num be-fee">
                    {parseFloat(block.feeTotal) > 0 ? formatCurrency(block.feeTotal) : '—'}
                  </td>
                  <td className="be-num">{formatCurrency(block.reserveBalance)}</td>
                  <td className="be-num">{formatCurrency(block.totalSupply)}</td>
                  <td>
                    <code className="be-hash" title={block.anchorHash}>
                      {truncateHash(block.anchorHash)}
                    </code>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Show more */}
      {blocks.length >= limit && (
        <div className="be-load-more">
          <button
            className="export-btn"
            onClick={() => setLimit((prev) => prev + 30)}
          >
            Load More
          </button>
        </div>
      )}
    </div>
  );
}

export default BlockExplorerPage;
